import { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";

// Components
import { TimePicker, ToggleSwitch } from "../../../../../components/UI";

// Builders
import { workDaysBuilder } from "../../../../../builders/general-branch-settings-manage-schedule.builder";

// Form Models
import { WorkDayFormModel } from "../../../../../models/app/forms";

// Calls
import {
  fetchWorkDays,
  updateWorkDays
} from "../../../../../api/calls/general-branch-settings.call";

// Styles
import { WorkingDaysStyles } from "./workingDays.styles";

const WorkingDays = ({ branchId, onDirtyChange, onSaveRef }) => {
  const [initalFormValues, setInitalFormValues] = useState([]);

  const getWorkDays = useCallback(async () => {
    const response = await fetchWorkDays(branchId);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    setInitalFormValues(
      response.workDays.map(day => new WorkDayFormModel(day))
    );
  }, [branchId]);

  useEffect(() => {
    getWorkDays();
  }, [getWorkDays]);

  const updateWorkingDaysHandler = async values => {
    const response = await updateWorkDays(values, branchId);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Branch work days successfully updated.");
    setInitalFormValues(
      response.workDays.map(day => new WorkDayFormModel(day))
    );
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initalFormValues,
    onSubmit: updateWorkingDaysHandler
  });

  const { values, handleSubmit, setValues } = formik;

  const hasChanges = useMemo(() => {
    if (values.length !== initalFormValues.length) return true;
    const sorted = [...values].sort((a, b) =>
      a.dayOfTheWeek.localeCompare(b.dayOfTheWeek)
    );
    const sortedInitial = [...initalFormValues].sort((a, b) =>
      a.dayOfTheWeek.localeCompare(b.dayOfTheWeek)
    );
    return sorted.some(
      (day, i) =>
        day.dayOfTheWeek !== sortedInitial[i].dayOfTheWeek ||
        day.opensAt !== sortedInitial[i].opensAt ||
        day.closesAt !== sortedInitial[i].closesAt
    );
  }, [values, initalFormValues]);

  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges, onDirtyChange]);

  useEffect(() => {
    onSaveRef?.(() => handleSubmit());
  }, [onSaveRef, handleSubmit]);

  const covertTimeStringToDateObject = timeString => {
    const parts = timeString.match(/(\d+):(\d+) (AM|PM)/);
    if (parts) {
      const startTime = new Date();
      let hours = parseInt(parts[1], 10);
      const minutes = parseInt(parts[2], 10);
      const tt = parts[3];

      if (tt === "PM" && hours < 12) hours += 12;
      if (tt === "AM" && hours === 12) hours = 0;

      const dateObject = new Date(startTime);
      dateObject.setHours(hours, minutes, 0, 0);

      return dateObject;
    }
    return new Date();
  };

  const handleTimeChange = (dateObject, dayOfTheWeek, fieldName) => {
    const hours = dateObject.getHours();
    const minutes = dateObject.getMinutes();

    const formatedHours =
      hours === 0
        ? "12"
        : hours > 12
        ? (hours - 12).toString().padStart(2, "0")
        : hours.toString().padStart(2, "0");
    const formatedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const amPm = hours < 12 ? "AM" : "PM";
    const formatedValue = `${formatedHours}:${formatedMinutes} ${amPm}`;

    setValues(
      values.map(value =>
        value.dayOfTheWeek === dayOfTheWeek
          ? new WorkDayFormModel({ ...value, [fieldName]: formatedValue })
          : value
      )
    );
  };

  const handleDayToggle = dayOfTheWeek => {
    const chosenWorkDay = workDaysBuilder.find(
      day => day.dayOfTheWeek === dayOfTheWeek
    );
    const chosenDays = values.map(day => day.dayOfTheWeek);
    if (chosenDays.includes(dayOfTheWeek)) {
      setValues(prevValues =>
        prevValues.filter(value => value.dayOfTheWeek !== dayOfTheWeek)
      );
      return;
    }

    setValues(prevValues => [...prevValues, chosenWorkDay]);
  };

  return (
    <WorkingDaysStyles>
      <form onSubmit={handleSubmit}>
        <div className="daysGrid">
          {workDaysBuilder.map((workDay, i) => {
            const chosenWorkDay = values.find(
              value => value.dayOfTheWeek === workDay.dayOfTheWeek
            );
            const isActive = !!chosenWorkDay;
            return (
              <div
                className={`dayCard ${isActive ? "active" : ""}`}
                key={i}
              >
                <div className="dayCardHeader">
                  <span className="dayName">{workDay.dayOfTheWeek}</span>
                  <ToggleSwitch
                    value={isActive}
                    name={workDay.dayOfTheWeek}
                    handleChange={() => handleDayToggle(workDay.dayOfTheWeek)}
                  />
                </div>
                <div className="timePickersSection">
                  {isActive && (
                    <>
                      <div className="timeField">
                        <TimePicker
                          value={covertTimeStringToDateObject(
                            chosenWorkDay.opensAt
                          )}
                          handleChange={date =>
                            handleTimeChange(
                              date,
                              workDay.dayOfTheWeek,
                              "opensAt"
                            )
                          }
                          dateFormat="hh : mm aa"
                        />
                      </div>
                      <span className="timeSeparator">—</span>
                      <div className="timeField">
                        <TimePicker
                          value={covertTimeStringToDateObject(
                            chosenWorkDay.closesAt
                          )}
                          handleChange={date =>
                            handleTimeChange(
                              date,
                              workDay.dayOfTheWeek,
                              "closesAt"
                            )
                          }
                          dateFormat="hh : mm aa"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </form>
    </WorkingDaysStyles>
  );
};

export default WorkingDays;
